import React, { FC, useMemo } from 'react';

/* Components */
import CourtFormsLandingEmptyView from '~/src/components/CourtForms/CourtFormsLandingEmptyView';
import FederalSection from '~/src/components/CourtForms/FederalSection';
import FederalCategoriesPreviewSection from '~/src/components/CourtForms/FederalCategoriesPreviewSection';
import StateSection from '~/src/components/CourtForms/StateSection';
import { stateCodeToStateName } from '~/src/utils/stateCodeToStateName';
import { DrillDownPanelButton } from '~/src/components/DrillDownPanel';
import FederalFormsWithLocalitiesContainer from '~/src/containers/FederalFormsWithLocalitiesContainer';
import AdditionalRegionSections from '~/src/components/CourtForms/AdditionalRegionSections';

/* Hooks */
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import { StateCode } from '~/src/entities/user';
import type { AdditionalRegionInfo } from '~/src/utils/additionalRegionForms';
import type { TemplateMetadata } from '~/src/hooks/useGetTemplateMetadata';

interface Props {
  handleStateFormsSelect: (s: keyof typeof stateCodeToStateName) => void;
  handleStateReginalFormsSelect: (s: keyof typeof stateCodeToStateName) => void;
  handleFederalCategorySelect?: (categoryLocality: string) => void;
  handleViewAllFederalCategories?: () => void;
  additionalRegions?: AdditionalRegionInfo[];
  templateMetadata?: TemplateMetadata;
  onAdditionalRegionProvinceSelect?: (
    region: AdditionalRegionInfo,
    filterCategory: string,
  ) => void;
}

const CourtFormsLandingWrapper: FC<Props> = ({
  handleStateFormsSelect,
  handleStateReginalFormsSelect,
  handleFederalCategorySelect,
  handleViewAllFederalCategories,
  additionalRegions = [],
  templateMetadata,
  onAdditionalRegionProvinceSelect,
}) => {
  const {
    isCourtFormsEnabled,
    isFederalCourtFormsEnabled,
    isFederalFormsCategoriesEnabled,
    doesFeatureAccessForThisStateCodeExist,
  } = useFeatureAccesses();

  const stateCourtFormSections = useMemo(
    () =>
      Object.entries(stateCodeToStateName)
        .filter((entry) =>
          doesFeatureAccessForThisStateCodeExist(entry[0] as StateCode),
        )
        .map(([stateCode, stateName]) => (
          <StateSection key={`${stateCode}_section`} stateName={stateName}>
            <DrillDownPanelButton
              key={`${stateCode}_form`}
              label={`${stateName} forms`}
              handleClick={() =>
                handleStateFormsSelect(
                  stateCode as keyof typeof stateCodeToStateName,
                )
              }
            />
            <DrillDownPanelButton
              key={`${stateCode}_locality`}
              label={`${stateName} regional forms`}
              handleClick={() =>
                handleStateReginalFormsSelect(
                  stateCode as keyof typeof stateCodeToStateName,
                )
              }
            />
          </StateSection>
        )),
    [
      doesFeatureAccessForThisStateCodeExist,
      handleStateFormsSelect,
      handleStateReginalFormsSelect,
    ],
  );

  const additionalRegionContent =
    onAdditionalRegionProvinceSelect && templateMetadata ? (
      <AdditionalRegionSections
        additionalRegions={additionalRegions}
        templateMetadata={templateMetadata}
        renderProvince={(region, province) => (
          <DrillDownPanelButton
            label={`${province} forms`}
            handleClick={() =>
              onAdditionalRegionProvinceSelect(region, province)
            }
          />
        )}
      />
    ) : null;

  if (!isCourtFormsEnabled && additionalRegions.length === 0) {
    return <CourtFormsLandingEmptyView />;
  }

  const hasOnlyFederalForms =
    isFederalCourtFormsEnabled && stateCourtFormSections.length === 0;
  const hasFederalAndStateForms =
    isFederalCourtFormsEnabled && stateCourtFormSections.length > 0;

  if (hasOnlyFederalForms && isFederalFormsCategoriesEnabled) {
    return (
      <div>
        {additionalRegionContent}
        <FederalFormsWithLocalitiesContainer
          showBreadcrumb={false}
          actionType="button"
          onCategorySelect={handleFederalCategorySelect}
        />
      </div>
    );
  }

  if (hasFederalAndStateForms && isFederalFormsCategoriesEnabled) {
    return (
      <div>
        {additionalRegionContent}
        <FederalCategoriesPreviewSection
          actionType="button"
          onCategorySelect={handleFederalCategorySelect}
          onViewAll={handleViewAllFederalCategories}
        />
        {stateCourtFormSections}
      </div>
    );
  }

  return (
    <div>
      {additionalRegionContent}
      {isFederalCourtFormsEnabled && (
        <FederalSection>
          <DrillDownPanelButton
            label="USCIS forms"
            handleClick={() => handleStateFormsSelect('FEDERAL')}
          />
        </FederalSection>
      )}
      {stateCourtFormSections}
    </div>
  );
};

export default CourtFormsLandingWrapper;
